import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { Navigate, NavLink, Outlet, useNavigate } from 'react-router-dom';

import LogoIcon from '../../../assets/images/Learnit_logo.svg';
import { landingFor, NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { logout } from '../../../requests';
import { getMyTeaching } from '../../../requests/opsInstructors';
import { fetchUser } from '../../../store/reducers/actionCreators';
import { clearCart } from '../../../store/reducers/userSlice';

/**
 * The instructor's shell — deliberately the smallest of the four: a
 * wordmark, the person's name, sign out, and the page. No ops nav, no
 * customer nav; an instructor's whole world here is their own classes.
 * Instructors only; the API enforces the same rule.
 */
export const TeachingLayout: React.FC = () => {
  const { user, isLoading } = useAppSelector((state) => state.userReducer);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(fetchUser());
  }, []);

  const isInstructor = user?.role === 'instructor';
  // Shares its cache with the classes page; a 403 (login not linked yet)
  // is the page's to explain, so no retries and no toast here.
  const { data: teaching } = useQuery({
    queryKey: ['teaching'],
    queryFn: getMyTeaching,
    enabled: isInstructor,
    retry: false,
  });

  if (isLoading) {
    return null;
  }
  if (!user) {
    return <Navigate to={PATH.login} replace />;
  }
  if (!isInstructor) {
    return <Navigate to={landingFor(user.role)} replace />;
  }

  // Same sign-out sequence as SidebarAccount and the learner header.
  const signOut = () => {
    localStorage.clear();
    dispatch(clearCart());
    logout().then(() => navigate(PATH.login, { replace: true }));
  };

  const name =
    teaching?.instructor.displayName ||
    [user.firstName, user.lastName].filter(Boolean).join(' ') ||
    user.email;

  return (
    <div className="min-h-screen bg-grey-50 text-gray-four">
      <header className="sticky top-0 z-30 border-b border-white-regular bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-3">
          <NavLink to={PATH.teaching} end className="flex items-center gap-3">
            <img src={LogoIcon} alt="Learn It" className="h-6 w-auto" />
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-bold">
              Teaching
            </span>
          </NavLink>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="truncate text-sm font-medium text-gray-five">
                {name || user.email}
              </div>
              <div className="truncate text-[11px] text-gray-bold">
                {user.email}
              </div>
            </div>
            <button
              type="button"
              onClick={signOut}
              className="rounded-lg px-3 py-1.5 text-xs text-gray-three transition-colors hover:bg-grey-50 hover:text-gray-four"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl">
        <Outlet />
      </main>
    </div>
  );
};

export default TeachingLayout;
